import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiCpu, FiSettings, FiTrash2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import { parametrosIAAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;



const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const HelpText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ParametroIAForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    id_cliente: '',
    ambiente: 'HML',
    ttl_padrao: 7200,
    ttl_sem_ia: 600,
    ia_modelo: 'gpt-4.1-mini',
    ia_system_prompt: '',
    ia_tone: 'formal',
    emojis: 'LOW',
    ia_limits_max_tokens: 6000,
    limits_requests_per_minute: 20,
    responder_por_audio: true,
    grupo_resumo_conversa: '',
    whatsapp_n8n: '',
    token: '',
    agent_name: ''
  });

  const [clientes, setClientes] = useState([]);
  const [tones, setTones] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);

  // Carregar dados do parâmetro se estiver editando
  useEffect(() => {
    if (isEditing) {
      carregarParametro();
    } else {
      // Iniciar com token vazio
      setFormData(prev => ({
        ...prev,
        token: ''
      }));
    }
  }, [id]);

  // Carregar clientes, tones e modelos
  useEffect(() => {
    carregarClientes();
    carregarTones();
    carregarModelos();
  }, []);

  const carregarParametro = async () => {
    try {
      setLoadingData(true);
      const response = await parametrosIAAPI.buscarPorId(id);
      
      if (response.data.success) {
        const parametro = response.data.data;
        setFormData({
          id_cliente: parametro.id_cliente || '',
          ambiente: parametro.ambiente || 'HML',
          ttl_padrao: parametro.ttl_padrao || 7200,
          ttl_sem_ia: parametro.ttl_sem_ia || 600,
          ia_modelo: parametro.ia_modelo || 'gpt-4.1-mini',
          ia_system_prompt: parametro.ia_system_prompt || '',
          ia_tone: parametro.ia_tone || 'formal',
          emojis: parametro.emojis || 'LOW',
          ia_limits_max_tokens: parametro.ia_limits_max_tokens || 6000,
          limits_requests_per_minute: parametro.limits_requests_per_minute || 20,
          responder_por_audio: parametro.responder_por_audio !== undefined ? parametro.responder_por_audio : true,
          grupo_resumo_conversa: parametro.grupo_resumo_conversa || '',
          whatsapp_n8n: parametro.whatsapp_n8n || '',
          token: parametro.token || '',
          agent_name: parametro.agent_name || ''
        });
      } else {
        setError('Erro ao carregar dados do parâmetro');
      }
    } catch (err) {
      console.error('Erro ao carregar parâmetro:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await parametrosIAAPI.listarClientes();
      if (response.data.success) {
        setClientes(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    }
  };

  const carregarTones = async () => {
    try {
      const response = await parametrosIAAPI.listarTones();
      if (response.data.success) {
        setTones(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar tones:', err);
    }
  };

  const carregarModelos = async () => {
    try {
      const response = await parametrosIAAPI.listarModelos();
      if (response.data.success) {
        setModelos(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleToneChange = async (tone) => {
    handleInputChange('ia_tone', tone);
    
    // Auto-gerar prompt sempre que o tom mudar
    try {
      setLoadingPrompt(true);
      const response = await parametrosIAAPI.gerarPrompt(tone, formData.id_cliente || null);
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          ia_tone: tone,
          ia_system_prompt: response.data.data.prompt
        }));
      }
    } catch (err) {
      console.error('Erro ao gerar prompt:', err);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações básicas
      if (!formData.id_cliente) {
        throw new Error('Cliente é obrigatório');
      }

      const dadosParaEnvio = {
        id_cliente: parseInt(formData.id_cliente),
        ambiente: formData.ambiente,
        ttl_padrao: parseInt(formData.ttl_padrao) || 7200,
        ttl_sem_ia: parseInt(formData.ttl_sem_ia) || 600,
        ia_modelo: formData.ia_modelo,
        ia_system_prompt: formData.ia_system_prompt,
        ia_tone: formData.ia_tone,
        emojis: formData.emojis || 'LOW',
        ia_limits_max_tokens: parseInt(formData.ia_limits_max_tokens) || 6000,
        limits_requests_per_minute: parseInt(formData.limits_requests_per_minute) || 20,
        responder_por_audio: formData.responder_por_audio,
        grupo_resumo_conversa: formData.grupo_resumo_conversa || null,
        whatsapp_n8n: formData.whatsapp_n8n || null,
        token: formData.token,
        agent_name: formData.agent_name || null,
        ativo: true // Sempre ativo por padrão
      };

      let response;
      if (isEditing) {
        response = await parametrosIAAPI.atualizar(id, dadosParaEnvio);
      } else {
        response = await parametrosIAAPI.criar(dadosParaEnvio);
      }

      if (response.data.success) {
        setSuccess(response.data.message || (isEditing ? 'Parâmetro atualizado com sucesso!' : 'Parâmetro criado com sucesso!'));
        setTimeout(() => {
          navigate('/parametros-ia');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar parâmetro');
      }
    } catch (err) {
      console.error('Erro ao salvar parâmetro:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/parametros-ia');
  };

  const handleExcluir = async () => {
    try {
      setDeleting(true);
      setError(null);

      await parametrosIAAPI.deletar(id);
      
      navigate('/parametros-ia');
    } catch (err) {
      console.error('Erro ao excluir parâmetro:', err);
      setError('Erro ao excluir parâmetro');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Parâmetro de IA' : 'Novo Parâmetro de IA'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Parâmetro de IA' : 'Novo Parâmetro de IA'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiCpu />
              Configurações Básicas
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="id_cliente">
                  Cliente <Required>*</Required>
                </Label>
                <Select
                  id="id_cliente"
                  value={formData.id_cliente}
                  onChange={(e) => handleInputChange('id_cliente', e.target.value)}
                  required
                  disabled={isEditing}
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome || cliente.nome_fantasia}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ambiente">
                  Ambiente <Required>*</Required>
                </Label>
                <Select
                  id="ambiente"
                  value={formData.ambiente}
                  onChange={(e) => handleInputChange('ambiente', e.target.value)}
                  required
                >
                  <option value="HML">Homologação (HML)</option>
                  <option value="PRD">Produção (PRD)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ia_modelo">Modelo de IA</Label>
                <Select
                  id="ia_modelo"
                  value={formData.ia_modelo}
                  onChange={(e) => handleInputChange('ia_modelo', e.target.value)}
                >
                  {modelos.map((modelo) => (
                    <option key={modelo.value} value={modelo.value}>
                      {modelo.value}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ia_tone">Tom de Voz</Label>
                <Select
                  id="ia_tone"
                  value={formData.ia_tone}
                  onChange={(e) => handleToneChange(e.target.value)}
                >
                  {tones.map((tone) => (
                    <option key={tone.value} value={tone.value}>
                      {tone.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="emojis">Uso de Emojis</Label>
                <Select
                  id="emojis"
                  value={formData.emojis}
                  onChange={(e) => handleInputChange('emojis', e.target.value)}
                >
                  <option value="NONE">NONE - Nunca usar emojis</option>
                  <option value="LOW">LOW - 0-1 emoji ocasionalmente</option>
                  <option value="MEDIUM">MEDIUM - 1-2 emojis em momentos-chave</option>
                  <option value="HIGH">HIGH - 2+ emojis, tom bem informal/animado</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="responder_por_audio">Responder por Áudio</Label>
                <Select
                  id="responder_por_audio"
                  value={formData.responder_por_audio ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('responder_por_audio', e.target.value === 'true')}
                >
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiSettings />
              Configurações de IA
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="ia_limits_max_tokens">Max Tokens</Label>
                <Input
                  id="ia_limits_max_tokens"
                  type="number"
                  placeholder="6000"
                  value={formData.ia_limits_max_tokens}
                  onChange={(e) => handleInputChange('ia_limits_max_tokens', e.target.value)}
                  min="100"
                  max="128000"
                />
                <HelpText>Limite máximo de tokens por resposta</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="limits_requests_per_minute">Requisições/Minuto</Label>
                <Input
                  id="limits_requests_per_minute"
                  type="number"
                  placeholder="20"
                  value={formData.limits_requests_per_minute}
                  onChange={(e) => handleInputChange('limits_requests_per_minute', e.target.value)}
                  min="1"
                  max="1000"
                />
                <HelpText>Limite de requisições por minuto</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ttl_padrao">TTL Padrão (segundos)</Label>
                <Input
                  id="ttl_padrao"
                  type="number"
                  placeholder="7200"
                  value={formData.ttl_padrao}
                  onChange={(e) => handleInputChange('ttl_padrao', e.target.value)}
                  min="60"
                />
                <HelpText>Tempo de vida do cache padrão</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ttl_sem_ia">TTL sem IA (segundos)</Label>
                <Input
                  id="ttl_sem_ia"
                  type="number"
                  placeholder="600"
                  value={formData.ttl_sem_ia}
                  onChange={(e) => handleInputChange('ttl_sem_ia', e.target.value)}
                  min="60"
                />
                <HelpText>Tempo de vida quando IA está desativada</HelpText>
              </FormGroup>

              <FormGroupFull>
                <Label htmlFor="ia_system_prompt">System Prompt</Label>
                <Textarea
                  id="ia_system_prompt"
                  placeholder="Digite o prompt do sistema para a IA..."
                  value={formData.ia_system_prompt}
                  onChange={(e) => handleInputChange('ia_system_prompt', e.target.value)}
                  rows={5}
                  disabled={loadingPrompt}
                />
                <HelpText>
                  {loadingPrompt ? 'Gerando prompt automaticamente...' : 'Instruções para o comportamento da IA (gerado automaticamente ao selecionar o Tom de Voz)'}
                </HelpText>
              </FormGroupFull>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiSettings />
              Configurações Adicionais
            </SectionTitle>
            
            <FormGrid>
            <FormGroup>
                <Label htmlFor="agent_name">Nome do Agente</Label>
                <Input
                  id="agent_name"
                  type="text"
                  placeholder="Nome do agente"
                  value={formData.agent_name}
                  onChange={(e) => handleInputChange('agent_name', e.target.value)}
                  maxLength={10}
                />
                <HelpText>Nome do agente no sistema (máximo 10 caracteres)</HelpText>
              </FormGroup>

              <FormGroupFull>
                <Label htmlFor="token">Token de Autenticação</Label>
                <Input
                  id="token"
                  type="text"
                  placeholder="Insira o token do dispositivo"
                  value={formData.token}
                  onChange={(e) => handleInputChange('token', e.target.value)}
                />
                <HelpText>Token de autenticação gerado pelo dispositivo</HelpText>
              </FormGroupFull>

              <FormGroup>
                <Label htmlFor="grupo_resumo_conversa">Grupo Resumo Conversa</Label>
                <Input
                  id="grupo_resumo_conversa"
                  type="text"
                  placeholder="5555999968677"
                  value={formData.grupo_resumo_conversa}
                  onChange={(e) => handleInputChange('grupo_resumo_conversa', e.target.value)}
                />
                <HelpText>Número do grupo para resumo de conversa</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="whatsapp_n8n">WhatsApp N8N</Label>
                <Input
                  id="whatsapp_n8n"
                  type="text"
                  placeholder="554299866387"
                  value={formData.whatsapp_n8n}
                  onChange={(e) => handleInputChange('whatsapp_n8n', e.target.value)}
                />
                <HelpText>Número do WhatsApp do N8N</HelpText>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={() => setShowDeleteModal(true)} $danger>
                <ButtonIcon>
                  <FiTrash2 />
                </ButtonIcon>
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Parâmetro de IA</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este parâmetro de IA? Esta ação não pode ser desfeita.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluir} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default ParametroIAForm;
